import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCalendarAlt,
  faCrown,
  faDollarSign,
  faMapMarkerAlt,
  faMobileScreenButton,
  faMoneyBillWave,
  faSync,
  faTrash,
  faUserFriends,
} from "@fortawesome/free-solid-svg-icons";
import { analyticsAPI } from "../../services/analyticsService";
import Loader from "../Loader";
import Styles from "../../styles/Analytics.module.css";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "../../utils/analyticsUtils";

const hasCurrentEntitlement = (subscription) => {
  if (!subscription) return false;
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  const expiry = isTrial
    ? subscription.trial_ends_at || subscription.subscription_expires_at
    : subscription.subscription_expires_at;
  return !expiry || new Date(expiry).getTime() > Date.now();
};

const subscriptionClass = (subscription) => {
  if (!subscription) return Styles.statusFree;
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(subscription)) return Styles.statusTrial;
  if (status === "active" && !isTrial && hasCurrentEntitlement(subscription)) {
    return Styles.statusPro;
  }
  return Styles.statusFree;
};

const subscriptionLabel = (subscription) => {
  if (!subscription) return "No subscription";
  const status = String(subscription.status || "").toLowerCase();
  const isTrial = subscription.is_trial || status === "trial";
  if (isTrial && hasCurrentEntitlement(subscription)) return "Trial";
  if (status === "active" && !isTrial && hasCurrentEntitlement(subscription)) {
    return subscription.plan_type === "yearly" ? "Annual active" : "Monthly active";
  }
  if (status === "active") return "Expired";
  return subscription.status || "Unknown";
};

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [referralData, setReferralData] = useState({ summary: {}, rows: [] });
  const [locationData, setLocationData] = useState({ rows: [] });
  const [savingId, setSavingId] = useState(null);
  const [roleDraft, setRoleDraft] = useState("user");
  const [grantDays, setGrantDays] = useState("30");
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;
    setLoading(true);
    setError("");
    setProfileData(null);

    try {
      const [result, referralResult, locationResult] = await Promise.all([
        analyticsAPI.getUserProfile(userId),
        analyticsAPI.getUserReferrals(userId),
        analyticsAPI.getUserLocations(userId),
      ]);
      if (requestId.current !== currentRequest) return;
      if (!result) {
        setError("User not found");
      } else {
        setProfileData(result);
        setReferralData(referralResult || { summary: {}, rows: [] });
        setLocationData(locationResult || { rows: [] });
      }
    } catch (loadError) {
      if (requestId.current === currentRequest) {
        setError(loadError.message || "Failed to load user profile");
      }
    } finally {
      if (requestId.current === currentRequest) {
        setLoading(false);
      }
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (profileData?.profile) {
      setRoleDraft(profileData.profile.role || "user");
    }
  }, [profileData?.profile]);

  const handleSaveRole = async () => {
    if (!profileData?.profile) return;

    setActionError("");
    setSavingId("role");

    try {
      const result = await analyticsAPI.setUserRole(
        profileData.profile.id,
        roleDraft,
      );
      if (!result?.success) {
        throw new Error(result?.error || "Failed to update role");
      }
      await load();
    } catch (roleError) {
      setActionError(roleError.message || "Failed to update role");
    } finally {
      setSavingId(null);
    }
  };

  const handleGrantPremium = async () => {
    if (!profileData?.profile) return;

    setActionError("");
    setSavingId("premium");

    try {
      const days = grantDays === "lifetime" ? null : Number(grantDays);
      const result = await analyticsAPI.grantPremium(
        profileData.profile.id,
        days,
      );
      if (!result?.success) {
        throw new Error(result?.error || "Failed to grant Pro");
      }
      await load();
    } catch (grantError) {
      setActionError(grantError.message || "Failed to grant Pro");
    } finally {
      setSavingId(null);
    }
  };

  const handleRevokePremium = async () => {
    if (!profileData?.profile) return;

    if (
      !window.confirm(
        "Revoke Pro access for this user? This does not cancel an App Store or Google Play subscription.",
      )
    ) {
      return;
    }

    setActionError("");
    setSavingId("premium");

    try {
      const result = await analyticsAPI.revokePremium(profileData.profile.id);
      if (!result?.success) {
        throw new Error(result?.error || "Failed to revoke Pro");
      }
      await load();
    } catch (revokeError) {
      setActionError(revokeError.message || "Failed to revoke Pro");
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteUser = async () => {
    if (
      !window.confirm(
        `Delete @${profileData?.profile?.username || "this user"}? This removes the account, posts, subscriptions, referrals, and app activity. This cannot be undone.`,
      )
    ) {
      return;
    }

    setActionError("");
    setSavingId("user");

    try {
      const result = await analyticsAPI.deleteUser(profileData.profile.id);
      if (!result?.success) {
        throw new Error(result?.error || "Failed to delete user");
      }
      navigate("/analytics/users");
    } catch (deleteError) {
      setActionError(deleteError.message || "Failed to delete user");
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;

    setActionError("");
    setSavingId(locationId);

    try {
      const result = await analyticsAPI.deleteLocation(locationId);
      if (!result?.success) {
        throw new Error(result?.error || "Failed to delete post");
      }
      await load();
    } catch (deleteError) {
      setActionError(deleteError.message || "Failed to delete post");
    } finally {
      setSavingId(null);
    }
  };

  const handleReferralPaidToggle = async (referral) => {
    setActionError("");
    setSavingId(referral.id);

    try {
      await analyticsAPI.setReferralPayout({
        referralId: referral.id,
        paid: !referral.paid,
        amount: referral.paid ? null : referral.payout_amount,
        currency: referral.payout_currency || "USD",
        reference: referral.payout_reference,
      });
      await load();
    } catch (payoutError) {
      setActionError(payoutError.message || "Failed to update payout");
    } finally {
      setSavingId(null);
    }
  };

  const revenueTotals = useMemo(
    () =>
      Object.entries(
        profileData?.activity?.gross_subscription_value_by_currency || {},
      ),
    [profileData],
  );

  if (loading && !profileData) {
    return (
      <div className={Styles.container}>
        <Loader text="Loading user profile" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className={Styles.container}>
        <div className={Styles.errorBox} role="alert">
          {error || "User not found"}
        </div>
        <button
          type="button"
          className={Styles.backButton}
          onClick={() => navigate("/analytics/users")}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to users
        </button>
      </div>
    );
  }

  const profile = profileData.profile;
  const activity = profileData.activity || {};
  const currentSubscription = profileData.current_subscription;
  const referrer = profileData.referrer;
  const referralSummary = referralData.summary || {};
  const referralRows = referralData.rows || [];
  const locations = locationData.rows || [];
  const payoutTotals = Object.entries(referralSummary.payout_totals || {});
  const subscriptionHistory = profileData.subscription_history || [];
  const subscriptionEvents = profileData.subscription_events || [];

  return (
    <div className={Styles.container}>
      <div className={Styles.headerRow}>
        <div>
          <p className={Styles.eyebrow}>User profile</p>
          <h1 className={Styles.title}>@{profile.username}</h1>
          <p className={Styles.welcomeMessage}>{profile.email}</p>
        </div>
        <div className={Styles.headerActions}>
          <button
            type="button"
            className={Styles.refreshButton}
            onClick={load}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            Refresh
          </button>
          <button
            type="button"
            className={Styles.backButton}
            onClick={() => navigate("/analytics/users")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            All users
          </button>
          <button
            type="button"
            className={Styles.dangerButton}
            onClick={handleDeleteUser}
            disabled={savingId === "user" || profile.role === "admin"}
            title={
              profile.role === "admin"
                ? "Admin accounts cannot be deleted from analytics"
                : "Delete this user"
            }
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete user
          </button>
        </div>
      </div>

      {error && (
        <div className={Styles.errorBox} role="alert">
          {error}
        </div>
      )}

      {actionError && (
        <div className={Styles.errorBox} role="alert">
          {actionError}
        </div>
      )}

      <div className={Styles.profileHero}>
        <div className={Styles.profileIdentity}>
          <span className={Styles.profileAvatar}>
            {(profile.username || "U").slice(0, 2).toUpperCase()}
          </span>
          <div>
            <h2>@{profile.username}</h2>
            <p>{profile.email}</p>
          </div>
        </div>
        <div className={Styles.badgeRow}>
          <span className={Styles.statusBadge}>{profile.role}</span>
          <span
            className={`${Styles.statusBadge} ${
              profile.is_premium ? Styles.statusPro : Styles.statusFree
            }`}
          >
            {profile.is_premium ? "Premium" : "Free"}
          </span>
          {profile.is_affiliate && (
            <span className={`${Styles.statusBadge} ${Styles.statusTrial}`}>
              Affiliate
            </span>
          )}
        </div>
      </div>

      <div className={Styles.card}>
        <div className={Styles.cardHeader}>
          <div>
            <h3 className={Styles.cardTitle}>Account Role</h3>
            <p className={Styles.cardSubtitle}>
              Managers can add locations and view referrals only.
            </p>
          </div>
        </div>
        <div className={Styles.headerActions}>
          <select
            className={Styles.rangeSelect}
            value={roleDraft}
            onChange={(event) => setRoleDraft(event.target.value)}
            aria-label="User role"
          >
            <option value="user">User</option>
            <option value="manager">Manager</option>
            <option value="admin">Admin</option>
          </select>
          <button
            type="button"
            className={Styles.tableAction}
            onClick={handleSaveRole}
            disabled={
              savingId === "role" ||
              roleDraft === profile.role
            }
          >
            Save role
          </button>
        </div>
      </div>

      <div className={Styles.card}>
        <div className={Styles.cardHeader}>
          <div>
            <h3 className={Styles.cardTitle}>Pro Access</h3>
            <p className={Styles.cardSubtitle}>
              Grant or revoke complimentary Pro access. Store subscriptions
              still need to be cancelled in the store.
            </p>
          </div>
        </div>
        <div className={Styles.headerActions}>
          <select
            className={Styles.rangeSelect}
            value={grantDays}
            onChange={(event) => setGrantDays(event.target.value)}
            aria-label="Pro grant duration"
          >
            <option value="30">30 days</option>
            <option value="365">1 year</option>
            <option value="lifetime">Lifetime</option>
          </select>
          <button
            type="button"
            className={Styles.tableAction}
            onClick={handleGrantPremium}
            disabled={savingId === "premium"}
          >
            Grant Pro
          </button>
          {profile.is_premium && (
            <button
              type="button"
              className={Styles.dangerButton}
              onClick={handleRevokePremium}
              disabled={savingId === "premium"}
            >
              Revoke Pro
            </button>
          )}
        </div>
      </div>

      <div className={Styles.statsGrid}>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#3b82f6" }}>
            <FontAwesomeIcon icon={faCalendarAlt} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>{formatDate(profile.created_at)}</span>
            <span className={Styles.statLabel}>Joined</span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#10b981" }}>
            <FontAwesomeIcon icon={faMapMarkerAlt} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {formatNumber(activity.locations)}
            </span>
            <span className={Styles.statLabel}>Locations added</span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#f97316" }}>
            <FontAwesomeIcon icon={faUserFriends} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {formatNumber(referralSummary.total_referrals)}
            </span>
            <span className={Styles.statLabel}>Referrals brought</span>
            <span className={Styles.statHint}>
              {formatNumber(referralSummary.paid_referrals)} paid •{" "}
              {formatNumber(referralSummary.unpaid_referrals)} unpaid
            </span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#0ea5e9" }}>
            <FontAwesomeIcon icon={faMobileScreenButton} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {formatNumber(activity.app_opens_30d)}
            </span>
            <span className={Styles.statLabel}>App opens in 30 days</span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#8b5cf6" }}>
            <FontAwesomeIcon icon={faMobileScreenButton} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {formatNumber(activity.screen_views_30d)}
            </span>
            <span className={Styles.statLabel}>Screen views in 30 days</span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#fbbf24" }}>
            <FontAwesomeIcon icon={faDollarSign} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {revenueTotals.length
                ? revenueTotals
                    .map(([currency, value]) => formatMoney(value, currency))
                    .join(" / ")
                : "$0"}
            </span>
            <span className={Styles.statLabel}>
              Subscription value (recorded + estimated)
            </span>
          </span>
        </div>
        <div className={Styles.statCard}>
          <span className={Styles.statIcon} style={{ color: "#10b981" }}>
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </span>
          <span className={Styles.statBody}>
            <span className={Styles.statValue}>
              {payoutTotals.length
                ? payoutTotals
                    .map(([currency, value]) => formatMoney(value, currency))
                    .join(" / ")
                : "$0"}
            </span>
            <span className={Styles.statLabel}>Affiliate payouts</span>
            <span className={Styles.statHint}>
              {formatNumber(referralSummary.subscribed_referrals)} subscribed
              referrals
            </span>
          </span>
        </div>
      </div>

      <div className={Styles.detailGrid}>
        <div className={Styles.card}>
          <h3 className={Styles.cardTitle}>Account Details</h3>
          <dl className={Styles.detailList}>
            <div>
              <dt>User ID</dt>
              <dd>{profile.id}</dd>
            </div>
            <div>
              <dt>Email verified</dt>
              <dd>{profile.email_verified ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt>Phone verified</dt>
              <dd>{profile.phone_verified ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt>Referral code</dt>
              <dd>{profile.referral_code || "Not set"}</dd>
            </div>
            <div>
              <dt>Installations</dt>
              <dd>{formatNumber(activity.installations)}</dd>
            </div>
            <div>
              <dt>First installed</dt>
              <dd>{formatDateTime(activity.first_installed_at)}</dd>
            </div>
            <div>
              <dt>Last app event</dt>
              <dd>{formatDateTime(activity.last_event_at)}</dd>
            </div>
            <div>
              <dt>Location views</dt>
              <dd>{formatNumber(activity.location_views)}</dd>
            </div>
          </dl>
        </div>

        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Current Subscription</h3>
              <p className={Styles.cardSubtitle}>Latest entitlement record</p>
            </div>
            <FontAwesomeIcon icon={faCrown} />
          </div>
          {currentSubscription ? (
            <dl className={Styles.detailList}>
              <div>
                <dt>Status</dt>
                <dd>
                  <span
                    className={`${Styles.statusBadge} ${subscriptionClass(
                      currentSubscription,
                    )}`}
                  >
                    {subscriptionLabel(currentSubscription)}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Plan</dt>
                <dd>{currentSubscription.plan_type || "—"}</dd>
              </div>
              <div>
                <dt>Started</dt>
                <dd>{formatDate(currentSubscription.subscription_started_at)}</dd>
              </div>
              <div>
                <dt>Expires</dt>
                <dd>
                  {formatDate(currentSubscription.subscription_expires_at)}
                </dd>
              </div>
              <div>
                <dt>Price</dt>
                <dd>
                  {currentSubscription.price_paid == null
                    ? "Not recorded"
                    : formatMoney(
                        currentSubscription.price_paid,
                        currentSubscription.currency || "USD",
                      )}
                </dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>{currentSubscription.source || "legacy"}</dd>
              </div>
            </dl>
          ) : (
            <p className={Styles.emptyText}>This user has never subscribed.</p>
          )}
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Referral Network</h2>
          <p className={Styles.sectionSubtitle}>
            Who referred this user and every user they referred.
          </p>
        </div>
      </div>

      <div className={Styles.detailGrid}>
        <div className={Styles.card}>
          <h3 className={Styles.cardTitle}>Referred By</h3>
          {referrer?.profile ? (
            <div className={Styles.personCard}>
              <div>
                <Link
                  className={Styles.personName}
                  to={`/analytics/users/${referrer.profile.id}`}
                >
                  @{referrer.profile.username}
                </Link>
                <span>{referrer.profile.email}</span>
              </div>
              <span
                className={`${Styles.statusBadge} ${subscriptionClass(
                  referrer.current_subscription,
                )}`}
              >
                {subscriptionLabel(referrer.current_subscription)}
              </span>
            </div>
          ) : (
            <p className={Styles.emptyText}>This user joined organically.</p>
          )}
        </div>

        <div className={Styles.card}>
          <div className={Styles.cardHeader}>
            <div>
              <h3 className={Styles.cardTitle}>Referred Users</h3>
              <p className={Styles.cardSubtitle}>
                {formatNumber(referralSummary.total_referrals)} total •{" "}
                {formatNumber(referralSummary.paid_referrals)} paid •{" "}
                {formatNumber(referralSummary.unpaid_referrals)} unpaid
              </p>
            </div>
            <FontAwesomeIcon icon={faUserFriends} />
          </div>
          {referralRows.length ? (
            <div className={Styles.personList}>
              {referralRows.map((entry) => (
                <div className={Styles.personCard} key={entry.id}>
                  <div>
                    <Link
                      className={Styles.personName}
                      to={`/analytics/users/${entry.referred_id}`}
                    >
                      @{entry.referred_username}
                    </Link>
                    <span>{entry.referred_email}</span>
                    {entry.referred_subscription && (
                      <span
                        className={`${Styles.statusBadge} ${subscriptionClass(
                          entry.referred_subscription,
                        )}`}
                      >
                        {subscriptionLabel(entry.referred_subscription)}
                      </span>
                    )}
                  </div>
                  <div className={Styles.personActions}>
                    <span
                      className={`${Styles.statusBadge} ${
                        entry.paid ? Styles.statusPro : Styles.statusFree
                      }`}
                    >
                      {entry.paid ? "Paid" : "Unpaid"}
                    </span>
                    {entry.payout_amount != null && (
                      <span className={Styles.topMeta}>
                        {formatMoney(
                          entry.payout_amount,
                          entry.payout_currency || "USD",
                        )}
                      </span>
                    )}
                    <button
                      type="button"
                      className={Styles.tableAction}
                      onClick={() => handleReferralPaidToggle(entry)}
                      disabled={savingId === entry.id}
                    >
                      {entry.paid ? "Unmark paid" : "Mark paid"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className={Styles.emptyText}>This user has not referred anyone.</p>
          )}
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Posts</h2>
          <p className={Styles.sectionSubtitle}>
            {formatNumber(locations.length)} posts • deleting a post removes its
            views and reports
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>Post</th>
                <th>Category</th>
                <th>Created</th>
                <th>Views</th>
                <th>Blocks</th>
                <th>Reported</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.id}>
                  <td>
                    <strong>{location.title}</strong>
                    {location.description && (
                      <span className={Styles.topMeta}>
                        {location.description}
                      </span>
                    )}
                  </td>
                  <td>{location.category || "—"}</td>
                  <td>{formatDate(location.created_at)}</td>
                  <td>{formatNumber(location.views)}</td>
                  <td>{formatNumber(location.blocks)}</td>
                  <td>{location.is_reported ? "Yes" : "No"}</td>
                  <td>
                    <button
                      type="button"
                      className={Styles.tableAction}
                      onClick={() => handleDeleteLocation(location.id)}
                      disabled={savingId === location.id}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {locations.length === 0 && (
                <tr>
                  <td colSpan="7" className={Styles.emptyTableCell}>
                    No posts for this user.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={Styles.sectionHeader}>
        <div>
          <h2 className={Styles.sectionTitle}>Subscription History</h2>
          <p className={Styles.sectionSubtitle}>
            Projection rows and immutable RevenueCat events
          </p>
        </div>
      </div>

      <div className={`${Styles.card} ${Styles.tableCard}`}>
        <div className={Styles.tableWrapper}>
          <table className={Styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Event</th>
                <th>Product</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {subscriptionEvents.map((event) => (
                <tr key={event.event_id}>
                  <td>{formatDateTime(event.occurred_at)}</td>
                  <td>{event.event_type}</td>
                  <td>{event.product_id || "—"}</td>
                  <td>{event.plan_type || "—"}</td>
                  <td>{event.is_trial ? "Trial" : event.status || "—"}</td>
                  <td>
                    {event.is_trial
                      ? "$0"
                      : formatMoney(event.revenue_amount, event.currency)}
                    {event.is_estimated && (
                      <span className={Styles.topMeta}>Estimated</span>
                    )}
                  </td>
                </tr>
              ))}
              {subscriptionEvents.length === 0 && (
                <tr>
                  <td colSpan="6" className={Styles.emptyTableCell}>
                    No subscription events recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {subscriptionHistory.length > 0 && (
        <details className={Styles.recordsDetails}>
          <summary>View {subscriptionHistory.length} subscription records</summary>
          <div className={Styles.tableWrapper}>
            <table className={Styles.table}>
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Product</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Trial</th>
                  <th>Expires</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionHistory.map((subscription) => (
                  <tr key={subscription.id}>
                    <td>{formatDateTime(subscription.created_at)}</td>
                    <td>{subscription.product_id}</td>
                    <td>{subscription.plan_type}</td>
                    <td>{subscription.status}</td>
                    <td>{subscription.is_trial ? "Yes" : "No"}</td>
                    <td>{formatDate(subscription.subscription_expires_at)}</td>
                    <td>{subscription.source || "legacy"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

export default UserProfile;
